/**
 * Autopilot API Service
 *
 * Handles all autopilot-related API calls
 */

import api from './api';

// Types

export type ScheduleType = 'daily' | 'weekly' | 'monthly';

/**
 * The cadence half of a create/update/preview request. Monthly requires a
 * day (1-28); the server 422s a monthly request without one
 * (`_require_day_for_monthly_cadence`), enforced on all three. Create and
 * update additionally reject a day sent alongside any other cadence
 * (`_reject_day_for_non_monthly_cadence`) — preview does not enforce that
 * half (see `PreviewBookingRequest`), but this type applies the same rule
 * everywhere for a uniform contract. Modeled as a union so a caller cannot
 * construct a monthly config and forget the day, or attach a day to a
 * daily/weekly one.
 */
export type ScheduleConfig =
  | { schedule_type: 'daily' | 'weekly'; schedule_day_of_month?: null }
  | { schedule_type: 'monthly'; schedule_day_of_month: number };

export interface QuotaBooking {
  autopilot_id: number | null;
  project_id: number | null;
  project_name: string | null;
  schedule_type: ScheduleType | null;
  prompt_count: number;
  amount: number;
  kind: 'initial' | 'renewal' | 'increase' | 'admin_adjustment';
}

export interface AutopilotQuota {
  monthly_limit: number;
  /** Booked by autopilots on activation — not refundable within the month. */
  reserved: number;
  /** Spent per run by manual tests and retests, net of refunds. */
  consumed: number;
  available: number;
  /** Full-month cost of all active autopilots, i.e. what next month will cost. */
  steady_state_cost: number;
  reset_at: string;
  usage_month: string;
  active_autopilots_count: number;
  bookings: QuotaBooking[];
  my_autopilots_count: number;
}

export interface BookingPreview {
  monthly_limit: number;
  initial_booking: number;
  full_month_cost: number;
  remaining_runs: number;
  available_now: number;
  available_steady: number;
  steady_state_cost: number;
  /**
   * Whether the configuration is inside the budget. The honest arithmetic,
   * always — the cost explanation is drawn from it whatever the server does
   * with it.
   */
  fits: boolean;
  /**
   * Whether the server would actually refuse a `fits: false` configuration,
   * i.e. `ENFORCE_MONTHLY_PROMPT_QUOTA`. While it is off the API accepts and
   * records an over-limit booking on purpose: the point of shipping with the
   * flag off is to learn which tenants a flip would block. A caller that
   * blocks on `fits` alone refuses what the server accepts and there is
   * nothing left to observe — so `fits` says what the numbers are and this
   * says whether they are a refusal.
   */
  enforced: boolean;
}

export interface Autopilot {
  id: number;
  project_id: number;
  project_name: string;
  autopilot_type: 'llm_score' | 'benchmark';
  is_active: boolean;
  prompt_count: number;
  schedule_type: ScheduleType;
  schedule_time: string | null;
  schedule_day_of_week: number | null;
  schedule_day_of_month: number | null;
  runs_after_llm: boolean;
  llm_config: any | null;
  benchmark_config: any | null;
  last_run_at: string | null;
  next_run_at: string | null;
  total_runs: number;
  created_at: string;
}

export interface AutopilotRun {
  id: number;
  autopilot_id: number;
  job_id: number | null;
  benchmark_id: number | null;
  triggered_at: string;
  /**
   * `GET /autopilots/{id}/runs` returns the rows unfiltered, and two of these
   * are open claims rather than outcomes: `check_and_run_autopilots` writes
   * 'pending' the moment it dispatches a slot, and the worker moves it to
   * 'running' when it arms the job. Both are on the wire long before any
   * terminal status is, so listing only the three outcomes made every
   * in-flight run a value the type says cannot exist. (Legacy rows carry
   * 'pending' too; `AUTOPILOT_RUN_STATUSES` in the backend models is the full
   * vocabulary the column permits.)
   */
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  error_message: string | null;
  skip_reason: string | null;
}

/**
 * `autopilot_type` is 'llm_score' and nothing else. `POST /autopilots` rejects
 * 'benchmark' before it does anything at all ("Benchmark-Autopiloten werden
 * nicht mehr unterstützt"), so allowing it here only bought a request that
 * could not succeed — with the type saying otherwise. `Autopilot` still admits
 * 'benchmark' because rows created before that rule exist and are listed.
 */
export type CreateAutopilotRequest = {
  project_id: number;
  autopilot_type: 'llm_score';
  llm_config?: any;
} & ScheduleConfig;

/**
 * Unlike create, the monthly-day rule can't be fully captured here: the
 * server falls back to the autopilot's *stored* schedule_type/day for
 * whichever half a request omits (`_validate_update_schedule`), so
 * `{}` alone is valid on a row that is already monthly. What the type below
 * still enforces, because it holds regardless of the stored row:
 * explicitly requesting 'monthly' requires a day, and explicitly requesting
 * 'daily'/'weekly' forbids one.
 */
export type UpdateAutopilotRequest = {
  llm_config?: any;
} & (
  | { schedule_type?: undefined; schedule_day_of_month?: number | null }
  | ScheduleConfig
);

/**
 * Input to `previewBooking`. Reuses `ScheduleConfig`, which is stricter here
 * than the server: `BookingPreviewRequest` (`autopilots.py`) only calls
 * `_require_day_for_monthly_cadence`, never the reject-check — a day sent
 * alongside 'daily'/'weekly' is silently ignored server-side for preview,
 * not rejected as it is for create/update. The stricter type is kept
 * anyway: preview has no persistence for a silently-dropped field to
 * corrupt, so the extra restriction only catches likely-mistaken input.
 */
export type PreviewBookingRequest = {
  prompt_count: number;
  autopilot_id?: number | null;
} & ScheduleConfig;

/**
 * Create/update commit their change first and read the quota back for the
 * response afterwards; if that second read fails, the request still
 * succeeds (201/200) but the response degrades instead of erroring for a
 * row that already exists. `quota_unavailable: true` marks that case so a
 * consumer can't quietly read `available`/`reserved`/`monthly_limit` as
 * `undefined` — it has to check the flag first.
 */
export type QuotaAfterMutation =
  | { quota_unavailable: true }
  | { quota_unavailable?: undefined; available: number; reserved: number; monthly_limit: number };

export type CreateAutopilotResponse = {
  id: number;
  prompt_count: number;
  next_run_at: string | null;
  message: string;
} & QuotaAfterMutation;

export type UpdateAutopilotResponse = {
  id: number;
  prompt_count: number;
  prompt_count_delta: number;
  message: string;
} & QuotaAfterMutation;

// User API Functions

export const getAutopilotQuota = async (tenantId?: number): Promise<AutopilotQuota> => {
  const params = tenantId ? { tenant_id: tenantId } : {};
  const response = await api.get('/autopilots/quota', { params });
  return response.data;
};

/**
 * `tenant_id` is honoured for admins only, exactly as on `GET /autopilots/quota`
 * — and it has to be passed wherever that one is. A create books against the
 * *project's* tenant (`autopilots.py` resolves the project first), so a preview
 * priced against the caller's own tenant would quote one budget while the
 * booking spends another: the preview would say it fits and the create would
 * still 400, which is the one thing the preview exists to prevent.
 */
export const previewBooking = async (
  input: PreviewBookingRequest,
  tenantId?: number
): Promise<BookingPreview> => {
  const params = tenantId ? { tenant_id: tenantId } : {};
  const response = await api.post('/autopilots/quota/preview', input, { params });
  return response.data;
};

export const listAutopilots = async (params?: {
  project_id?: number;
  tenant_id?: number; // v4.8.3 - needed for admin users viewing tenant projects
  autopilot_type?: string;
  is_active?: boolean;
}): Promise<Autopilot[]> => {
  const response = await api.get('/autopilots', { params });
  return response.data;
};

export const getAutopilot = async (id: number): Promise<Autopilot> => {
  const response = await api.get(`/autopilots/${id}`);
  return response.data;
};

export const createAutopilot = async (data: CreateAutopilotRequest): Promise<CreateAutopilotResponse> => {
  const response = await api.post('/autopilots', data);
  return response.data;
};

export const updateAutopilot = async (id: number, data: UpdateAutopilotRequest): Promise<UpdateAutopilotResponse> => {
  const response = await api.put(`/autopilots/${id}`, data);
  return response.data;
};

export const toggleAutopilot = async (id: number, is_active: boolean): Promise<any> => {
  const response = await api.patch(`/autopilots/${id}/toggle`, { is_active });
  return response.data;
};

export const deleteAutopilot = async (id: number): Promise<any> => {
  const response = await api.delete(`/autopilots/${id}`);
  return response.data;
};

export const getAutopilotRuns = async (
  id: number,
  limit: number = 50,
  offset: number = 0
): Promise<AutopilotRun[]> => {
  const response = await api.get(`/autopilots/${id}/runs`, {
    params: { limit, offset }
  });
  return response.data;
};

// Admin API Functions

/**
 * `GET /admin/tenants/{id}/autopilot-quota` spreads `get_tenant_quota_info`
 * plus `tenant_name` — it does NOT add `my_autopilots_count`, which only the
 * user-facing `GET /autopilots/quota` computes (it counts autopilots created
 * by the *current* user, which has no meaning for an admin viewing another
 * tenant). `Omit` rather than `AutopilotQuota & {...}` so a consumer reading
 * `.my_autopilots_count` on this response is a compile error, not a
 * `my_autopilots_count: undefined` at runtime.
 */
export type TenantAutopilotQuota = Omit<AutopilotQuota, 'my_autopilots_count'> & {
  tenant_name: string;
};

export const getTenantAutopilotQuota = async (
  tenantId: number
): Promise<TenantAutopilotQuota> => {
  const response = await api.get(`/admin/tenants/${tenantId}/autopilot-quota`);
  return response.data;
};

// `updateTenantAutopilotQuota` was removed in migration 070 — base quota is
// fixed at 10 for all tenants and increases come exclusively from billing modules.
